import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import SkeletonTable from "../../Common/SkeletonTable";
import ErrorCard from "../../Common/ErrorCard";
import EmptyCard from "../../Common/EmptyCard";
import PaginationBar from "../../Common/PaginationBar";
import { inventoryFetcher } from "../../../network/InventoryService";
import { DEFAULT_PAGE_SIZE } from "../../../utils/inventoryHelper";

const RollOutList = ({ searchTerm }) => {
  const [page, setPage] = useState(1);
  const dispatchNotification = useNotification();

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: page - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchTerm) {
      params.append("search_term", searchTerm);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-rollout?${params.toString()}`;
  }, [page, searchTerm]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const rollOutData = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const copyToClipboard = async (text) => {
    if (!text && text !== "") return false;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        try {
          const textarea = document.createElement("textarea");
          textarea.value = text;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "absolute";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          const successful = document.execCommand("copy");
          document.body.removeChild(textarea);
          return successful;
        } catch {
          return false;
        }
      }
    }
  };

  const handleCopy = async (label, text) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      dispatchNotification({
        type: "SUCCESS",
        message: `${label} copied to clipboard`,
      });
    } else {
      dispatchNotification({
        type: "ERROR",
        message: `Failed to copy ${label}`,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={6}
          rowCount={7}
          headers={[
            "#",
            "Mapping ID",
            "Upload Date",
            "Uploaded By",
            "Eligible Merchant Count",
            "Binded Device",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          title="Error loading roll out data"
          message="Failed to load roll out data. Please try again."
          onRetryClick={mutate}
        />
      );
    }

    if (rollOutData.length === 0) {
      return (
        <EmptyCard
          title="No roll out mappings found"
          message={
            searchTerm
              ? "No roll out mappings found matching your search criteria."
              : "No roll out mapping data available."
          }
        />
      );
    }

    return (
      <table className="inventory-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Mapping ID</th>
            <th>Upload Date</th>
            <th>Uploaded By</th>
            <th>Eligible Merchant Count</th>
            <th>Binded Device</th>
            {/* <th>Action</th> */}
          </tr>
        </thead>
        <tbody>
          {rollOutData.map((rollOut, index) => (
            <tr key={rollOut.rollout_id || index} className="table-row">
              <td>{(page - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td className="mapping-id">
                <div
                  className="copyable"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCopy("Mapping ID", rollOut.rollout_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCopy("Mapping ID", rollOut.rollout_id);
                    }
                  }}
                >
                  {rollOut.rollout_id}
                  <ContentCopyIcon
                    style={{ fontSize: 16, color: "#E2136E", marginLeft: 8 }}
                  />
                </div>
              </td>
              <td>{formatDate(rollOut.created__date)}</td>
              <td
                className="uploaded-by"
                title={rollOut.uploaded_by__username || ""}
              >
                <div className="uploader-info">
                  <div className="uploader-name">
                    {rollOut.uploaded_by__username || "Unknown"}
                  </div>
                  <div className="uploader-email">
                    ({rollOut.uploaded_by__email || "No email"})
                  </div>
                </div>
              </td>
              <td>{rollOut.eligible_merchant_count?.toLocaleString() || 0}</td>
              <td>{rollOut.binded_count?.toLocaleString() || 0}</td>
              {/* <td>
                <button className="details-btn">Download List</button>
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="inventory-content-container">
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}
    </div>
  );
};

export default RollOutList;
